import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GOLD, ACCENT_GOLD, PARCHMENT, PANEL_BG, CONTENT_BG_DEFAULT, FourCorners,
} from '../lib/medievalTheme';
import AchievementOrnamentIcon from './AchievementOrnamentIcon';
import HeraldSeal from './ui/HeraldSeal';
import { KeystoneIcon } from './ui/icons';
import { getAchievement } from '../utils/arena/achievements';
import { onAchievementToast } from '../utils/arena/achievementToasts';
import { playAchievementHerald } from '../utils/arena/uiSounds';
import { t } from '../localization/t';

// Herald toast. Beat one of the achievement reward arc. Announces an
// unlock the moment it happens with a compact bottom-right banner; the
// full-screen AchievementClaimOverlay (beat two) remains the payoff and
// still requires a deliberate click. Clicking this toast IS that click.
//
// The medallion deliberately reuses the claim overlay's coin-face
// recipe (gold rim, recessed stone interior, per-achievement ornament
// mask) at toast scale, and hangs off the card's left edge like a wax
// seal: the unlock moment and the ceremony read as one object seen
// twice, and no other toast breaks its own silhouette.
//
// Feed it via pushAchievementToast(id). See achievementToasts.js.
// Shows one banner at a time; further ids queue and play through
// back-to-back. The herald sound cue fires once per batch (when the
// queue goes empty → non-empty), never per toast. Repetition is the
// fastest way to make a reward sound cheap.

const AUTO_DISMISS_MS = 6000;
const MOTE_COUNT = 7;

const itemVariants = {
  hidden: { opacity: 0, y: 12, filter: 'blur(4px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' },
};

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export default class AchievementUnlockToast extends Component {
  state = {
    currentId: null,
    queue: [],
  };

  componentDidMount() {
    this._unsubscribe = onAchievementToast(this.handlePush);
  }

  componentWillUnmount() {
    this._unsubscribe?.();
    clearTimeout(this._timer);
  }

  componentDidUpdate(prevProps) {
    // Ceremony (or another blocking overlay) just closed. Resume the queue.
    if (prevProps.paused && !this.props.paused) this._advance();
  }

  handlePush = (achievementId) => {
    const batchStart = !this.state.currentId && this.state.queue.length === 0;
    if (batchStart) playAchievementHerald();
    this.setState(
      (s) => ({ queue: [...s.queue, achievementId] }),
      this._advance,
    );
  };

  _advance = () => {
    if (this.props.paused) return;
    if (this.state.currentId) return;
    const [next, ...rest] = this.state.queue;
    if (!next) return;
    // Fresh mote burst per showing. Angles are decorative, so random
    // is fine; keyed on the id so re-renders don't rescatter them.
    this._motes = Array.from({ length: MOTE_COUNT }, () => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 2 + Math.random() * 1.5;
      return {
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
        delay: Math.random() * 0.15,
      };
    });
    this.setState({ currentId: next, queue: rest });
    this._startTimer(AUTO_DISMISS_MS);
  };

  _startTimer = (ms) => {
    clearTimeout(this._timer);
    this._deadline = Date.now() + ms;
    this._timer = setTimeout(this._dismiss, ms);
  };

  // Hover pauses the clock; leaving restarts it with whatever was left
  // (floored so a toast can't vanish the instant the cursor slips off).
  handleMouseEnter = () => {
    clearTimeout(this._timer);
    this._remaining = Math.max(0, this._deadline - Date.now());
  };

  handleMouseLeave = () => {
    if (!this.state.currentId) return;
    this._startTimer(Math.max(1500, this._remaining ?? 0));
  };

  _dismiss = () => {
    clearTimeout(this._timer);
    this.setState({ currentId: null });
  };

  handleClick = () => {
    const id = this.state.currentId;
    this._dismiss();
    // No-ops server-side if the id isn't actually unclaimed (e.g. the
    // admin test banner for an already-claimed achievement). The toast
    // just dismisses.
    this.props.onClaimRequest?.(id);
  };

  render() {
    const { currentId } = this.state;
    const achievement = currentId ? getAchievement(currentId) : null;
    const reduced = prefersReducedMotion();

    return (
      <div
        data-screenshot-ignore="true"
        className="fixed bottom-4 right-4 z-[150] pointer-events-none"
      >
        <AnimatePresence onExitComplete={this._advance}>
          {achievement && !this.props.paused ? (
            <motion.div
              key={currentId}
              role="button"
              tabIndex={0}
              data-sound="none"
              className="pointer-events-auto relative cursor-pointer"
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, y: 12, filter: 'blur(4px)', transition: { duration: 0.15, ease: 'easeIn' } }}
              transition={{ type: 'spring', stiffness: 260, damping: 24 }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
              onClick={this.handleClick}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.handleClick(); }}
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
              style={{
                width: '20cqw',
                // Left padding clears the overhanging medallion.
                padding: '0.9cqw 1.1cqw 0.9cqw 2.9cqw',
                background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
                backgroundBlendMode: 'overlay, normal',
                borderRadius: 'var(--radius-lg)',
                boxShadow: [
                  '0 16px 48px rgba(0,0,0,0.6)',
                  `0 0 24px ${GOLD} 0.18)`,
                  `inset 0 0 0 1px ${GOLD} 0.28)`,
                ].join(', '),
                isolation: 'isolate',
              }}
            >
              <FourCorners color={`${GOLD} 0.55)`} />

              {/* Foil sheen. One left-to-right pass after the text has
                  settled, clipped to the card so it never crosses the
                  overhanging medallion. */}
              {!reduced ? (
                <div
                  className="absolute inset-0 overflow-hidden pointer-events-none"
                  style={{ borderRadius: 'var(--radius-lg)' }}
                >
                  <motion.div
                    className="absolute inset-0"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ delay: 0.7, duration: 0.6, ease: 'easeInOut' }}
                    style={{
                      background: 'linear-gradient(105deg, transparent 40%, rgba(255,230,160,0.16) 50%, transparent 60%)',
                      mixBlendMode: 'screen',
                    }}
                  />
                </div>
              ) : null}

              {/* Wax-seal medallion, hanging off the left edge. */}
              <div
                className="absolute flex items-center justify-center"
                style={{
                  width: '3.6cqw',
                  height: '3.6cqw',
                  left: '-1.5cqw',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                {!reduced ? this._motes?.map((m, i) => (
                  <motion.div
                    key={i}
                    className="absolute rounded-full"
                    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                    animate={{ x: `${m.x}cqw`, y: `${m.y}cqw`, opacity: 0, scale: 0.25 }}
                    transition={{ duration: 0.9, delay: m.delay, ease: [0.22, 0.8, 0.36, 1] }}
                    style={{
                      width: '0.3cqw',
                      height: '0.3cqw',
                      background: ACCENT_GOLD,
                      boxShadow: `0 0 6px ${GOLD} 0.85)`,
                    }}
                  />
                )) : null}
                <motion.div
                  initial={reduced ? false : { scale: 0.25, opacity: 0, filter: 'blur(4px)' }}
                  animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }}
                  transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                >
                  <HeraldSeal size="3.6cqw">
                    <AchievementOrnamentIcon
                      achievementId={achievement.id}
                      color="rgba(244, 215, 136, 0.95)"
                      opacity={0.92}
                      style={{ position: 'absolute', inset: '12%', width: 'auto', height: 'auto' }}
                    />
                  </HeraldSeal>
                </motion.div>
              </div>

              {/* Split-and-staggered text column. */}
              <motion.div
                className="flex flex-col"
                initial={reduced ? false : 'hidden'}
                animate="visible"
                variants={{ visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } }}
                style={{ gap: '0.2cqw' }}
              >
                <motion.div
                  variants={itemVariants}
                  className="font-bold"
                  style={{
                    fontSize: '0.55cqw',
                    letterSpacing: '0.28cqw',
                    color: `${PARCHMENT} 0.85)`,
                    textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                  }}
                >
                  {t('Achievement Unlocked')}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="arena-heading font-bold"
                  style={{
                    fontSize: '1.05cqw',
                    lineHeight: 1.15,
                    textWrap: 'balance',
                    background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 55%, #b9893a 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))',
                  }}
                >
                  {achievement.name}
                </motion.div>
                <motion.div
                  variants={itemVariants}
                  className="flex items-center font-semibold"
                  style={{
                    gap: '0.4cqw',
                    marginTop: '0.15cqw',
                    fontSize: '0.55cqw',
                    letterSpacing: '0.12cqw',
                    color: '#c9a86a',
                  }}
                >
                  <KeystoneIcon size={13} />
                  <span>{t('Claim your Keystone')}</span>
                </motion.div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
}
